// Market research workspace (step 2): the full list of
// market_signal findings with descriptions + citations, manual paste-in,
// per-finding delete, run / re-run, and the done-state toggle.

import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  ExternalLink,
  Plus,
  Sparkles,
  Check,
  AlertCircle,
  Trash2,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useProject, useUpdateProject } from '@/hooks/use-projects';
import { useCanvasData } from '@/hooks/use-canvas-data';
import { useMarketResearchStatus, useRunMarketResearch } from '@/hooks/use-market-research';
import { api } from '@/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { GENERATION_THRESHOLDS, thresholdFriction } from '@/lib/generation-friction';
import { WORKSPACE_WIDTH } from '@/lib/workspace-width';
import { StepHeader } from '@/features/project/StepHeader';
import { PlanLimitError, planLimitMessage, allowanceLabel, CONTACT_PATH } from '@/lib/plan-limit';
import type { NodeMetadata } from '@/services/canvas-mappers';
import type { Node as ReactFlowNode } from '@xyflow/react';
import { groupFindingsByCategory, CATEGORY_SECTIONS, type MarketCategory } from '@/lib/market-categories';
import { SectionLabel } from '@/components/ui/section-label';

// Citations are user-controllable (paste-in) and get rendered into an
// <a href>. Only http(s) URLs are safe to linkify — a javascript:/data:
// scheme would be a clickable XSS vector. Used both to validate on save and
// to guard the render sink (which also shows AI-sourced citations).
function isSafeHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

interface MarketResearchViewProps {
  projectId: string;
}

export function MarketResearchView({ projectId }: MarketResearchViewProps) {
  const { data: project } = useProject(projectId);
  const updateProject = useUpdateProject();
  const { data: canvas } = useCanvasData(projectId);
  const statusQuery = useMarketResearchStatus(projectId);
  const runMutation = useRunMarketResearch();

  const reviewed = !!project?.marketReviewed;
  const isRunning = statusQuery.data?.status === 'running';
  const lastError = statusQuery.data?.status === 'failed' ? statusQuery.data?.error : undefined;

  const signals = useMemo(
    () => (canvas?.nodes ?? []).filter((n) => n.type === 'market_signal'),
    [canvas?.nodes],
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Owned here rather than in SignalList so the expand control can sit in the
  // toolbar row alongside the finding count.
  const [expandAll, setExpandAll] = useState(false);
  const [expandSeq, setExpandSeq] = useState(0);
  const friction = thresholdFriction(signals.length, GENERATION_THRESHOLDS.findings);

  // Allowance (owner's plan). null label = unlimited or unknown → show nothing.
  const runsUsed = project?.usage?.researchRuns ?? 0;
  const runsLimit = project?.limits?.researchRunsPerProject;
  const runsLabel = allowanceLabel(runsUsed, runsLimit, 'research run', 'research runs');
  const atRunLimit = runsLimit !== null && runsLimit !== undefined && runsUsed >= runsLimit;

  const handleRun = async () => {
    try {
      await runMutation.mutateAsync(projectId);
    } catch (err) {
      if (err instanceof PlanLimitError) {
        const m = planLimitMessage(err.payload);
        toast({ title: m.title, description: m.description, variant: 'destructive' });
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to start research';
      toast({ title: 'Research failed to start', description: message, variant: 'destructive' });
    }
  };

  const handleMarkReviewed = async () => {
    try {
      await updateProject.mutateAsync({ projectId, patch: { marketReviewed: true } });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark complete';
      toast({ title: 'Save failed', description: message, variant: 'destructive' });
    }
  };

  const handleReopen = async () => {
    try {
      await updateProject.mutateAsync({ projectId, patch: { marketReviewed: false } });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to re-open step';
      toast({ title: 'Save failed', description: message, variant: 'destructive' });
    }
  };

  const runDisabled = isRunning || runMutation.isPending || !project?.idea || atRunLimit;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className={`${WORKSPACE_WIDTH} mx-auto space-y-6`}>

        <StepHeader
          projectId={projectId}
          stepId="market"
          actions={
            <div className="flex items-center gap-3 shrink-0">
              {runsLabel && (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {runsLabel}
                  {atRunLimit && (
                    <>
                      {' · '}
                      <a href={CONTACT_PATH} className="underline underline-offset-2">
                        Talk to us
                      </a>
                    </>
                  )}
                </span>
              )}
              <Button
                variant={reviewed ? 'outline' : 'brand'}
                onClick={() => (friction === null ? handleRun() : setConfirmOpen(true))}
                disabled={runDisabled}
              >
                {runMutation.isPending || isRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                {signals.length > 0 ? 'Run again' : 'Run market research'}
              </Button>
              {reviewed ? (
                <Button variant="outline" onClick={handleReopen} disabled={updateProject.isPending}>
                  Re-open step
                </Button>
              ) : signals.length > 0 && !isRunning ? (
                <Button variant="outline" onClick={handleMarkReviewed} disabled={updateProject.isPending}>
                  <Check className="h-4 w-4 mr-2" />
                  Mark step complete
                </Button>
              ) : null}
            </div>
          }
        />

        {!project?.idea && (
          <p className="text-sm text-muted-foreground">
            Capture your idea in step 1 first, so the scan has something to research.
          </p>
        )}

        {lastError && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-destructive/30 bg-destructive/10 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <div>
              <div className="font-medium">Research failed</div>
              <div>{lastError}</div>
            </div>
          </div>
        )}

        {isRunning && (
          <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>
              Researching. This can take a few minutes, and you can leave this page; findings appear
              here when they're ready.
            </span>
          </div>
        )}

        {/* One toolbar row: the count and the expand control were each
            floating on their own otherwise-empty line, which left a band of
            dead space between the header and the first finding. */}
        <div className="flex items-center justify-between gap-3">
          <PasteFindingButton projectId={projectId} disabled={isRunning} />
          {signals.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>
                {signals.length} {signals.length === 1 ? 'finding' : 'findings'}
              </span>
              <span aria-hidden="true">·</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 text-xs text-muted-foreground"
                onClick={() => {
                  setExpandAll((v) => !v);
                  setExpandSeq((v) => v + 1);
                }}
              >
                {expandAll ? 'Collapse all' : 'Expand all'}
              </Button>
            </div>
          )}
        </div>

        {signals.length === 0 && !isRunning && (
          <div className="rounded-lg border bg-card p-12 text-center">
            <Sparkles className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <h2 className="font-semibold">No findings yet</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Run market research, or add a finding you've collected yourself.
            </p>
          </div>
        )}
        {/* Sections wait for the first finding: while the first run is in
            flight there is nothing to group yet, and the running banner above
            already carries that state. */}
        {signals.length > 0 && (
          <SignalList
            signals={signals}
            projectId={projectId}
            expandSignal={expandSeq}
            expandTo={expandAll}
          />
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Your findings list is already long"
        body={`You have ${friction ?? 0} findings already. Another run will bury the ones you haven't reviewed.`}
        confirmLabel="Run anyway"
        onConfirm={() => {
          setConfirmOpen(false);
          void handleRun();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function SignalList({
  signals,
  projectId,
  expandSignal,
  expandTo,
}: {
  signals: ReactFlowNode[];
  projectId: string;
  // Rows clamp their finding text by default. The expand/collapse control
  // lives in the toolbar above, so its state is owned by the parent and
  // broadcast down here — no per-row state to lift.
  expandSignal: number;
  expandTo: boolean;
}) {
  const sections = useMemo(
    () =>
      groupFindingsByCategory(
        signals,
        (s) => (s.data as { metadata?: NodeMetadata } | undefined)?.metadata?.category,
      ),
    [signals],
  );
  return (
    <div className="space-y-5">
      {sections.map(({ section, items }) => (
        <section key={section.key} aria-labelledby={`findings-${section.key}`} className="space-y-2">
          <div className="space-y-0.5">
            <SectionLabel id={`findings-${section.key}`}>
              {section.heading}
              {items.length > 0 && (
                <span className="ml-1.5 font-normal normal-case tracking-normal text-muted-foreground">
                  · {items.length}
                </span>
              )}
            </SectionLabel>
            <p className="text-xs text-muted-foreground">{section.blurb}</p>
          </div>
          {items.length === 0 ? (
            // A thin category is information, so it stays visible as one quiet
            // line rather than disappearing.
            <p className="text-xs text-muted-foreground/70">No {section.name.toLowerCase()} findings yet</p>
          ) : (
            <ul className="space-y-2">
              {items.map((signal) => (
                <SignalRow
                  key={signal.id}
                  signal={signal}
                  projectId={projectId}
                  expandSignal={expandSignal}
                  expandTo={expandTo}
                />
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

function SignalRow({
  signal,
  projectId,
  expandSignal,
  expandTo,
}: {
  signal: ReactFlowNode;
  projectId: string;
  expandSignal: number;
  expandTo: boolean;
}) {
  const queryClient = useQueryClient();
  const data = signal.data as { label?: string; content?: string; metadata?: NodeMetadata } | undefined;
  const meta = data?.metadata;
  const isPasted = meta?.source === 'paste';
  const citations = Array.isArray((meta as Record<string, unknown> | undefined)?.citations)
    ? ((meta as { citations?: Array<{ url: string; title?: string }> }).citations ?? [])
    : [];

  const [deleting, setDeleting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (expandSignal > 0) setExpanded(expandTo);
  }, [expandSignal, expandTo]);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const response = await api.deleteNode(signal.id, projectId);
      if (!response.success) throw new Error(response.message || 'Failed to delete');
      await queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete finding';
      toast({ title: 'Delete failed', description: message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <li className="rounded-md border bg-card shadow-xs p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-0.5 shrink-0 text-muted-foreground"
          aria-label={expanded ? 'Collapse finding' : 'Expand finding'}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm leading-snug">{data?.label || 'Untitled finding'}</span>
            {/* Research is the default origin, so badging it labelled almost
                every card and the badge stopped carrying information. Only the
                exception — a finding the PM pasted in — is worth marking. */}
            {isPasted && (
              <Badge tone="neutral" className="uppercase tracking-wide">Paste</Badge>
            )}
            {!expanded && citations.length > 0 && (
              <span className="shrink-0 text-xs text-muted-foreground">
                {citations.length} source{citations.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {data?.content && (
            // Finding text IS the content, so it stays visible — clamped to two
            // lines until expanded rather than hidden behind the chevron.
            <p className={`mt-1 text-xs text-muted-foreground whitespace-pre-wrap ${expanded ? '' : 'line-clamp-2'}`}>
              {data.content}
            </p>
          )}
          {expanded && citations.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {citations.map((c, i) => (
                <li key={i} className="text-xs">
                  {isSafeHttpUrl(c.url) ? (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {c.title || c.url}
                    </a>
                  ) : (
                    // Non-http(s) scheme — render as plain text, never a link.
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <ExternalLink className="h-3 w-3" />
                      {c.title || c.url}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={handleDelete}
          disabled={deleting}
          aria-label="Delete finding"
        >
          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </li>
  );
}

interface PasteFindingButtonProps {
  projectId: string;
  disabled?: boolean;
}

function PasteFindingButton({ projectId, disabled }: PasteFindingButtonProps) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={disabled}>
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add finding manually
      </Button>
    );
  }
  return <PasteFindingForm projectId={projectId} onDone={() => setOpen(false)} />;
}

interface PasteFindingFormProps {
  projectId: string;
  onDone: () => void;
}

function PasteFindingForm({ projectId, onDone }: PasteFindingFormProps) {
  const queryClient = useQueryClient();
  const { data: canvas } = useCanvasData(projectId);

  // Parent picker options: same set the AI uses (job/challenge/opportunity/idea).
  const parents = useMemo(() => {
    const nodes = canvas?.nodes ?? [];
    return nodes
      .filter((n) => ['job', 'challenge', 'opportunity', 'idea'].includes(n.type ?? ''))
      .map((n) => ({
        id: n.id,
        type: n.type as string,
        title: ((n.data as { label?: string } | undefined)?.label ?? '').toString(),
      }))
      .filter((p) => p.title.length > 0);
  }, [canvas?.nodes]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [citationsText, setCitationsText] = useState('');
  const [category, setCategory] = useState<MarketCategory | ''>('');
  const [parentNodeId, setParentNodeId] = useState<string>(parents[0]?.id ?? '');
  const [saving, setSaving] = useState(false);

  // Default the parent picker once the candidates list arrives (canvas
  // data may load after the form mounts). Setting state during render
  // would trip React's "Cannot update during render" warning and risk
  // re-render loops.
  useEffect(() => {
    if (!parentNodeId && parents.length > 0) {
      setParentNodeId(parents[0].id);
    }
  }, [parents, parentNodeId]);

  const handleSave = async () => {
    const t = title.trim();
    if (!t) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }
    if (!parentNodeId) {
      toast({
        title: 'No parent available',
        description: 'Capture your idea in step 1 first so the finding has somewhere to attach.',
        variant: 'destructive',
      });
      return;
    }
    // One URL per line in the textarea; trimmed, empty lines dropped. Only
    // http(s) URLs are kept — a pasted javascript:/data: URL would otherwise
    // be persisted and rendered into an <a href>, an XSS vector.
    const citations = citationsText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .filter(isSafeHttpUrl)
      .map((url) => ({ url }));

    setSaving(true);
    try {
      const nodeResp = await api.createNode({
        projectId,
        type: 'market_signal',
        title: t,
        description: description.trim() || undefined,
        metadata: {
          source: 'paste',
          citations,
          // Optional: an untagged paste lands in the Uncategorised section.
          ...(category ? { category } : {}),
        },
      });
      if (!nodeResp.success || !nodeResp.data) {
        throw new Error(nodeResp.message || 'Failed to create finding');
      }
      const newNodeId = (nodeResp.data as { id: string }).id;

      const edgeResp = await api.createEdge({
        projectId,
        sourceNodeId: newNodeId,
        targetNodeId: parentNodeId,
        edgeType: 'evidences',
      });
      if (!edgeResp.success) {
        // The node landed but has no evidences edge — it'd be an orphan
        // finding. Best-effort delete so a retry starts clean.
        await api.deleteNode(newNodeId, projectId).catch(() => undefined);
        throw new Error(edgeResp.message || 'Failed to link finding');
      }

      await queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
      onDone();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save finding';
      toast({ title: 'Save failed', description: message, variant: 'destructive' });
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-3 rounded-md border bg-card p-3">
      <Field label="Title">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What did you find?"
          maxLength={500}
          className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
        />
      </Field>
      <Field label="Description" hint="optional">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder="Substance of the finding and why it matters"
          className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-ring resize-none"
        />
      </Field>
      <Field label="Citations" hint="one URL per line">
        <textarea
          value={citationsText}
          onChange={(e) => setCitationsText(e.target.value)}
          rows={2}
          placeholder="https://example.com/source"
          className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-ring resize-none"
        />
      </Field>
      <Field label="Category" hint="optional">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as MarketCategory | '')}
          className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
        >
          <option value="">Not sure yet</option>
          {CATEGORY_SECTIONS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Attach to" hint="pick the node this finding bears on">
        <select
          value={parentNodeId}
          onChange={(e) => setParentNodeId(e.target.value)}
          className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
          disabled={parents.length === 0}
        >
          {parents.length === 0 ? (
            <option value="">(no parent nodes available)</option>
          ) : (
            parents.map((p) => (
              <option key={p.id} value={p.id}>
                {p.type}: {p.title}
              </option>
            ))
          )}
        </select>
      </Field>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving || !title.trim() || !parentNodeId}>
          {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          Save finding
        </Button>
        <Button variant="outline" size="sm" onClick={onDone} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <div>
        <span className="text-sm font-medium">{label}</span>
        {hint && <span className="text-xs text-muted-foreground ml-2">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
